import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuthStore } from './stores/authStore';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CheckEmailPage from './pages/CheckEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ErrorBoundary from './components/ErrorBoundary';
import NotFoundPage from './pages/NotFoundPage';
import HelpPage from './pages/HelpPage';
import PublicVendorProfile from './pages/PublicVendorProfile';
import VendorProfileAnalytics from './pages/vendor/VendorProfileAnalytics';
import PublicDirectory from './pages/PublicDirectory';
import ActivatePage from './pages/ActivatePage';
import AdminClaims from './pages/admin/AdminClaims';
import VendorDisputes from './pages/vendor/VendorDisputes';
import AdminDisputes from './pages/admin/AdminDisputes';
import VendorOnboarding from './pages/onboarding/VendorOnboarding';
import ClientOnboarding from './pages/onboarding/ClientOnboarding';

import NotificationsPage from './pages/NotificationsPage';
import VendorDashboard from './pages/vendor/VendorDashboard';
import VendorSites from './pages/vendor/VendorSites';
import VendorSiteDetail from './pages/vendor/VendorSiteDetail';
import VendorGuards from './pages/vendor/VendorGuards';
import VendorGuardDetail from './pages/vendor/VendorGuardDetail';
import VendorGuardForm from './pages/vendor/VendorGuardForm';
import VendorSettings from './pages/vendor/VendorSettings';
import VendorConnections from './pages/vendor/VendorConnections';
import VendorUploads from './pages/vendor/VendorUploads';
import VendorAnalytics from './pages/vendor/VendorAnalytics';
import VendorScoringHistory from './pages/vendor/VendorScoringHistory';
import VendorGuardAssignment from './pages/vendor/VendorGuardAssignment';
import VendorSiteInvitations from './pages/vendor/VendorSiteInvitations';
import VendorOrganization from './pages/vendor/VendorOrganization';
import VendorProfile from './pages/vendor/VendorProfile';
import VendorBilling from './pages/vendor/VendorBilling';
import VendorBillingPlans from './pages/vendor/VendorBillingPlans';
import VendorBillingInvoices from './pages/vendor/VendorBillingInvoices';
import VendorBillingPayment from './pages/vendor/VendorBillingPayment';
import VendorBillingSuccess from './pages/vendor/VendorBillingSuccess';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientSites from './pages/client/ClientSites';
import ClientSiteDetail from './pages/client/ClientSiteDetail';
import ClientSiteForm from './pages/client/ClientSiteForm';
import ClientVendors from './pages/client/ClientVendors';
import ClientVendorDetail from './pages/client/ClientVendorDetail';
import ClientSettings from './pages/client/ClientSettings';
import ClientVendorDirectory from './pages/client/ClientVendorDirectory';
import ClientAnalytics from './pages/client/ClientAnalytics';
import ClientProfile from './pages/client/ClientProfile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminVendors from './pages/admin/AdminVendors';
import AdminVendorDetail from './pages/admin/AdminVendorDetail';
import AdminClients from './pages/admin/AdminClients';
import AdminClientDetail from './pages/admin/AdminClientDetail';
import AdminSites from './pages/admin/AdminSites';
import AdminSiteDetail from './pages/admin/AdminSiteDetail';
import AdminGuards from './pages/admin/AdminGuards';
import AdminGuardDetail from './pages/admin/AdminGuardDetail';
import AdminRules from './pages/admin/AdminRules';
import AdminRuleDetail from './pages/admin/AdminRuleDetail';
import AdminRuleForm from './pages/admin/AdminRuleForm';
import AdminIngestion from './pages/admin/AdminIngestion';
import AdminMetrics from './pages/admin/AdminMetrics';
import AdminPilotSummary from './pages/admin/AdminPilotSummary';
import AdminAudit from './pages/admin/AdminAudit';

// Marketing pages — code-split so dashboard visitors don't pay the
// landing-bundle cost.  Note: the reverse isn't true yet — the
// dashboard route tree is still eagerly imported above, so landing
// visitors still ship the full app bundle.  Lazy-loading the
// dashboard tree is a separate, larger refactor.
const PublicLayout = lazy(() => import('./components/landing/PublicLayout'));
const HomePage = lazy(() => import('./pages/landing/HomePage'));
const ScoringPage = lazy(() => import('./pages/landing/ScoringPage'));
const FeaturesPage = lazy(() => import('./pages/landing/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/landing/PricingPage'));
const AboutPage = lazy(() => import('./pages/landing/AboutPage'));
const ContactPage = lazy(() => import('./pages/landing/ContactPage'));
const TermsPage = lazy(() => import('./pages/landing/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/landing/PrivacyPage'));

// Routes that don't need to wait for user loading.  Exact-match
// against the route prefix (NOT startsWith with bare '/' — that
// matches every URL).  Per Opus design review B1.
const PUBLIC_ROUTE_PREFIXES = [
  '/login', '/signup', '/forgot-password', '/check-email',
  '/verify-email', '/reset-password',
  // Marketing surfaces
  '/scoring', '/features', '/pricing', '/about', '/contact',
  '/terms', '/privacy', '/directory', '/v/', '/activate',
];

function LandingSuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function AppRoutes() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const loadUser = useAuthStore((s) => s.loadUser);
  const location = useLocation();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const isPublicPath =
    location.pathname === '/'  // home is public
    || PUBLIC_ROUTE_PREFIXES.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
    );

  // Feature 12 — first-login wizard redirect.
  const needsWizard =
    user
    && user.role !== 'admin'
    && user.setup_completed_at === null;
  const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
  const justLandedAtDashboardRoot =
    needsWizard && normalizedPath.toLowerCase() === `/${user.role}`;
  if (justLandedAtDashboardRoot) {
    return <Navigate to={`/onboarding/${user!.role}`} replace />;
  }

  if (isLoading && isAuthenticated && !user && !isPublicPath) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const dashboardPath = user
    ? (user.role === 'admin' ? '/admin' : `/${user.role}`)
    : '/login';

  return (
    <Routes>
      {/* Feature 1 — Public vendor profile (no auth required) */}
      <Route path="/v/:slug" element={<PublicVendorProfile />} />
      {/* Feature 14 — activation page (no auth) */}
      <Route path="/activate/:token" element={<ActivatePage />} />

      {/* Feature 12 — first-login setup wizard (auth required; outside dashboard layout) */}
      <Route path="/onboarding/vendor" element={
        <ProtectedRoute allowedRoles={['vendor']}><VendorOnboarding /></ProtectedRoute>
      } />
      <Route path="/onboarding/client" element={
        <ProtectedRoute allowedRoles={['client']}><ClientOnboarding /></ProtectedRoute>
      } />

      {/* Auth pages — wrapped in AuthLayout (centered card + logo) */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={isAuthenticated && user ? <Navigate to={dashboardPath} replace /> : <LoginPage />} />
        <Route path="/signup" element={isAuthenticated && user ? <Navigate to={dashboardPath} replace /> : <SignUpPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      </Route>

      {/* Marketing site — wrapped in PublicLayout (top nav + footer).
          Per Opus design review B2, only `/` redirects logged-in
          users to their dashboard.  All other marketing pages stay
          accessible — a logged-in client legit wants to read /pricing
          to compare upgrades, share /scoring with a teammate, etc.
          The nav swaps Log in / Sign up for "Back to dashboard" when
          the user is already authenticated. */}
      <Route element={
        <Suspense fallback={<LandingSuspenseFallback />}><PublicLayout /></Suspense>
      }>
        <Route path="/" element={
          isAuthenticated && user
            ? <Navigate to={dashboardPath} replace />
            : <Suspense fallback={<LandingSuspenseFallback />}><HomePage /></Suspense>
        } />
        <Route path="/scoring"   element={<Suspense fallback={<LandingSuspenseFallback />}><ScoringPage /></Suspense>} />
        <Route path="/features"  element={<Suspense fallback={<LandingSuspenseFallback />}><FeaturesPage /></Suspense>} />
        <Route path="/pricing"   element={<Suspense fallback={<LandingSuspenseFallback />}><PricingPage /></Suspense>} />
        <Route path="/directory" element={<PublicDirectory />} />
        <Route path="/about"     element={<Suspense fallback={<LandingSuspenseFallback />}><AboutPage /></Suspense>} />
        <Route path="/contact"   element={<Suspense fallback={<LandingSuspenseFallback />}><ContactPage /></Suspense>} />
        <Route path="/terms"     element={<Suspense fallback={<LandingSuspenseFallback />}><TermsPage /></Suspense>} />
        <Route path="/privacy"   element={<Suspense fallback={<LandingSuspenseFallback />}><PrivacyPage /></Suspense>} />
      </Route>

      {/* Vendor routes — sidebar + top bar */}
      <Route element={<ProtectedRoute allowedRoles={['vendor']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/vendor" element={<VendorDashboard />} />
        <Route path="/vendor/sites" element={<VendorSites />} />
        <Route path="/vendor/sites/:id" element={<VendorSiteDetail />} />
        <Route path="/vendor/guards" element={<VendorGuards />} />
        <Route path="/vendor/guards/new" element={<VendorGuardForm />} />
        <Route path="/vendor/guards/:id" element={<VendorGuardDetail />} />
        <Route path="/vendor/guards/:id/edit" element={<VendorGuardForm />} />
        <Route path="/vendor/connections" element={<Navigate to="/vendor/site-invitations" replace />} />
        <Route path="/vendor/uploads" element={<VendorUploads />} />
        <Route path="/vendor/settings" element={<VendorSettings />} />
        <Route path="/vendor/analytics" element={<VendorAnalytics />} />
        <Route path="/vendor/profile-analytics" element={<VendorProfileAnalytics />} />
        <Route path="/vendor/disputes" element={<VendorDisputes />} />
        <Route path="/vendor/scoring" element={<VendorScoringHistory />} />
        <Route path="/vendor/guard-assignment" element={<VendorGuardAssignment />} />
        <Route path="/vendor/site-invitations" element={<VendorSiteInvitations />} />
        <Route path="/vendor/organization" element={<VendorOrganization />} />
        <Route path="/vendor/profile" element={<VendorProfile />} />
        <Route path="/vendor/billing" element={<VendorBilling />} />
        <Route path="/vendor/billing/plans" element={<VendorBillingPlans />} />
        <Route path="/vendor/billing/invoices" element={<VendorBillingInvoices />} />
        <Route path="/vendor/billing/payment" element={<VendorBillingPayment />} />
        <Route path="/vendor/billing/success" element={<VendorBillingSuccess />} />
        <Route path="/vendor/notifications" element={<NotificationsPage />} />
        <Route path="/vendor/help" element={<HelpPage />} />
      </Route>

      {/* Client routes — sidebar + top bar */}
      <Route element={<ProtectedRoute allowedRoles={['client']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/client" element={<ClientDashboard />} />
        <Route path="/client/sites" element={<ClientSites />} />
        <Route path="/client/sites/new" element={<ClientSiteForm />} />
        <Route path="/client/sites/:id" element={<ClientSiteDetail />} />
        <Route path="/client/sites/:id/edit" element={<ClientSiteForm />} />
        <Route path="/client/vendors" element={<ClientVendors />} />
        <Route path="/client/vendors/:id" element={<ClientVendorDetail />} />
        <Route path="/client/directory" element={<ClientVendorDirectory />} />
        <Route path="/client/settings" element={<ClientSettings />} />
        <Route path="/client/analytics" element={<ClientAnalytics />} />
        <Route path="/client/profile" element={<ClientProfile />} />
        <Route path="/client/scoring" element={<VendorScoringHistory />} />
        <Route path="/client/notifications" element={<NotificationsPage />} />
        <Route path="/client/help" element={<HelpPage />} />
      </Route>

      {/* Admin routes — sidebar + top bar */}
      <Route element={<ProtectedRoute allowedRoles={['admin']}><DashboardLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/vendors" element={<AdminVendors />} />
        <Route path="/admin/vendors/:id" element={<AdminVendorDetail />} />
        <Route path="/admin/clients" element={<AdminClients />} />
        <Route path="/admin/clients/:id" element={<AdminClientDetail />} />
        <Route path="/admin/sites" element={<AdminSites />} />
        <Route path="/admin/sites/:id" element={<AdminSiteDetail />} />
        <Route path="/admin/guards" element={<AdminGuards />} />
        <Route path="/admin/guards/:id" element={<AdminGuardDetail />} />
        <Route path="/admin/rules" element={<AdminRules />} />
        <Route path="/admin/rules/new" element={<AdminRuleForm />} />
        <Route path="/admin/rules/:id" element={<AdminRuleDetail />} />
        <Route path="/admin/rules/:id/edit" element={<AdminRuleForm />} />
        <Route path="/admin/ingestion" element={<AdminIngestion />} />
        <Route path="/admin/metrics" element={<AdminMetrics />} />
        <Route path="/admin/pilot-summary" element={<AdminPilotSummary />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
        <Route path="/admin/claims" element={<AdminClaims />} />
        <Route path="/admin/disputes" element={<AdminDisputes />} />
        <Route path="/admin/notifications" element={<NotificationsPage />} />
        <Route path="/admin/help" element={<HelpPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
